import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import { MESSAGE } from "@/constants/message";
import {
  DataSettings,
  EDataSettingPointType,
  EDataSettingSpatialType
} from "@/types/dataSetting";

import AttributeSelection from "./AttributeSelection";
import { NO_GEOMETRY_OPTIONS, POINT_TYPE_OPTIONS } from "./dataSettingOptions";

interface NoGeometrySettingProps {
  dataSetting: DataSettings | null;
  attributesLatitude: IOptionItem[];
  attributesLongitude: IOptionItem[];
  attributesForAdminArea: IOptionItem[];
  handleSpatialAggregationSelect: <
    K extends keyof NonNullable<DataSettings["spatialAggregation"]>
  >(
    key: K,
    value: NonNullable<DataSettings["spatialAggregation"]>[K]
  ) => void;
}

const NoGeometrySetting = ({
  dataSetting,
  attributesLatitude,
  attributesLongitude,
  attributesForAdminArea,
  handleSpatialAggregationSelect
}: NoGeometrySettingProps) => (
  <div className="flex flex-col">
    <Dropdown
      className="mt-4"
      placeholder="Spatial Aggregation*"
      data={NO_GEOMETRY_OPTIONS}
      value={dataSetting?.spatialAggregation?.type || null}
      onSelect={(data) => {
        if (!data) return;
        handleSpatialAggregationSelect("point", undefined);
        handleSpatialAggregationSelect("adminAreaAttribute", undefined);
        handleSpatialAggregationSelect("type", data);
      }}
    />
    {dataSetting?.spatialAggregation?.type?.value ===
      EDataSettingSpatialType.Admin_Area && (
      <AttributeSelection
        label="Attribute Selection"
        description={MESSAGE.NO_GEOM_ADMIN_AREA}
        items={attributesForAdminArea}
        selectedValue={dataSetting.spatialAggregation?.adminAreaAttribute ?? ""}
        onSelect={(value) =>
          handleSpatialAggregationSelect("adminAreaAttribute", value)
        }
      />
    )}
    {dataSetting?.spatialAggregation?.type?.value ===
      EDataSettingSpatialType.Point && (
      <div className="mt-4">
        <Dropdown
          placeholder="Type*"
          data={POINT_TYPE_OPTIONS}
          value={dataSetting?.spatialAggregation?.point?.pointType || null}
          onSelect={(data) => {
            if (!data) return;
            handleSpatialAggregationSelect("point", { pointType: data });
          }}
        />
        {dataSetting?.spatialAggregation?.point?.pointType?.value ===
          EDataSettingPointType.Coordinate && (
          <PointCoordinateSelection
            attributesLatitude={attributesLatitude}
            attributesLongitude={attributesLongitude}
            dataSetting={dataSetting}
            handleSpatialAggregationSelect={handleSpatialAggregationSelect}
          />
        )}
        {dataSetting?.spatialAggregation?.point?.pointType?.value ===
          EDataSettingPointType.Geocoding && (
          <AttributeSelection
            label="Attribute Selection"
            description={MESSAGE.NO_GEOM_POINT_GEOCODING}
            items={attributesForAdminArea}
            selectedValue={
              dataSetting.spatialAggregation?.point?.geocodingAtrtribute ?? ""
            }
            onSelect={(value) =>
              handleSpatialAggregationSelect("point", {
                ...dataSetting?.spatialAggregation?.point,
                geocodingAtrtribute: value
              })
            }
          />
        )}
      </div>
    )}
  </div>
);

interface PointCoordinateSelectionProps {
  dataSetting: DataSettings | null;
  attributesLatitude: IOptionItem[];
  attributesLongitude: IOptionItem[];
  handleSpatialAggregationSelect: <
    K extends keyof NonNullable<DataSettings["spatialAggregation"]>
  >(
    key: K,
    value: NonNullable<DataSettings["spatialAggregation"]>[K]
  ) => void;
}

const PointCoordinateSelection: React.FC<PointCoordinateSelectionProps> = ({
  dataSetting,
  attributesLatitude,
  attributesLongitude,
  handleSpatialAggregationSelect
}) => (
  <div className="flex gap-2 mt-4 w-full justify-between">
    <Dropdown
      className="w-full"
      placeholder="Latitude Attribute*"
      data={attributesLatitude}
      value={
        dataSetting?.spatialAggregation?.point?.pointCoordinate?.latAttribute ||
        null
      }
      onSelect={(data) => {
        if (!data) return;
        handleSpatialAggregationSelect("point", {
          ...dataSetting?.spatialAggregation?.point,
          pointCoordinate: {
            ...dataSetting?.spatialAggregation?.point?.pointCoordinate,
            latAttribute: data
          }
        });
      }}
    />
    <Dropdown
      className="w-full"
      placeholder="Longitude Attribute*"
      data={attributesLongitude}
      value={
        dataSetting?.spatialAggregation?.point?.pointCoordinate
          ?.longAttribute || null
      }
      onSelect={(data) => {
        if (!data) return;
        handleSpatialAggregationSelect("point", {
          ...dataSetting?.spatialAggregation?.point,
          pointCoordinate: {
            ...dataSetting?.spatialAggregation?.point?.pointCoordinate,
            longAttribute: data
          }
        });
      }}
    />
  </div>
);

export default NoGeometrySetting;
